import { Component, ElementRef, inject, OnInit, ViewChild } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormControl, ReactiveFormsModule, Validators } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialogModule, MatDialogRef } from '@angular/material/dialog';
import { MatRippleModule } from '@angular/material/core';
import { Router } from '@angular/router';
import { SharedModule } from '../shared.module';
import { TMSService } from '../../core/service/tms/tms.service';
import { TmsSocketService } from '../../core/service/tms-socket/tms-socket.service';
import { LocalStorageService } from '../../core/service/localStorage/local-storage.service';
import { LoaderService } from '../../core/service/loader.service';
import { PopupServiceService } from '../../core/service/popup/popup-service.service';
import { ProfileService } from '../../core/service/profile/profile.service';
import { TMS_AWAY_OPTIONS } from '../../core/constants/tms.const';
import { TodayOverviewComponent } from '../today-overview/today-overview.component';

export interface AwayOption {
  label: string;
  icon: string;
  color: string;
  key: string;
}

export interface AwayStatusDialogData {
  // 'login' forces mandatory MPIN setup right after sign-in, with no option to skip.
  // 'resetMpin' opens the MPIN setup form on demand (e.g. from Profile) even if an MPIN already exists.
  // 'away' (default) is the normal "Mark as Away" flow.
  context?: 'away' | 'login' | 'resetMpin';
}

@Component({
  selector: 'app-away-status',
  standalone: true,
  imports: [CommonModule, ReactiveFormsModule, SharedModule, MatDialogModule, MatRippleModule, TodayOverviewComponent],
  templateUrl: './away-status.component.html',
  styleUrl: './away-status.component.scss',
})
export class AwayStatusComponent implements OnInit {
  private dialogRef = inject(MatDialogRef<AwayStatusComponent>);
  private dialogData = inject<AwayStatusDialogData | null>(MAT_DIALOG_DATA, { optional: true });
  private router = inject(Router);
  private tmsService = inject(TMSService);
  private tmsSocketService = inject(TmsSocketService);
  private localStorageService = inject(LocalStorageService);
  private loader = inject(LoaderService);
  private popup = inject(PopupServiceService);
  private profileService = inject(ProfileService);

  // "Other" is appended here (rather than in TMS_AWAY_OPTIONS) so it can be handled
  // as a special case — picking it reveals a mandatory free-text reason instead of
  // starting the break immediately.
  private static readonly _OTHER_OPTION_KEY = 'other';
  readonly awayOptions: AwayOption[] = [
    ...TMS_AWAY_OPTIONS,
    { key: AwayStatusComponent._OTHER_OPTION_KEY, label: 'Other', icon: 'edit_note', color: '#6B7280' },
  ];

  showOtherReason = false;
  otherReasonControl = new FormControl('', {
    nonNullable: true,
    validators: [Validators.required, Validators.minLength(3), Validators.maxLength(500)],
  });
  @ViewChild('otherReasonBlock') otherReasonBlock?: ElementRef<HTMLElement>;

  // Opened right after login to force mandatory MPIN setup (no skipping, no away-reason grid)
  readonly isLoginFlow = this.dialogData?.context === 'login';

  // Opened on demand (e.g. from Profile) to let an already-verified user reset their existing MPIN
  readonly isResetFlow = this.dialogData?.context === 'resetMpin';

  // Only false when the login API explicitly reports the MPIN hasn't been set yet
  isMpinAdded = true;
  currentUserData: any;
  mpinDigits: string[] = ['', '', '', ''];
  mpinControl = new FormControl('', [Validators.required, Validators.pattern(/^\d{4}$/)]);

  async ngOnInit(): Promise<void> {
    this.currentUserData = await this.localStorageService.getKeyIndexedDbKey('currentUser');
    this.isMpinAdded = this.currentUserData?.isMpinAdded;

    // Reset flow always shows the MPIN setup form, even though an MPIN already exists
    if (this.isResetFlow) {
      this.isMpinAdded = false;
      return;
    }

    // Defensive: nothing to do if this was opened for the login flow but MPIN is already set
    if (this.isLoginFlow && this.isMpinAdded) {
      this.dialogRef.close();
    }
  }

  allowOnlyNumbers(event: KeyboardEvent): boolean {
    const charCode = event.which ? event.which : event.keyCode;
    if (charCode < 48 || charCode > 57) {
      event.preventDefault();
      return false;
    }
    return true;
  }

  onMpinDigitInput(event: Event, index: number, nextInput: HTMLInputElement | null): void {
    const input = event.target as HTMLInputElement;
    const digit = input.value.replace(/\D/g, '').slice(-1);
    input.value = digit;
    this.mpinDigits[index] = digit;
    this.mpinControl.setValue(this.mpinDigits.join(''));
    this.mpinControl.markAsTouched();

    if (digit && nextInput) {
      nextInput.focus();
    }
  }

  onMpinDigitKeydown(event: KeyboardEvent, index: number, prevInput: HTMLInputElement | null): void {
    const input = event.target as HTMLInputElement;
    if (event.key === 'Backspace' && !input.value && prevInput) {
      prevInput.focus();
    }
  }

  saveMpin(): void {
    if (this.mpinControl.invalid) {
      this.mpinControl.markAsTouched();
      return;
    }

    const userId = this.currentUserData?.id;
    if (!userId) return;

    this.loader.show();
    this.profileService.updateUserDetails(userId, {
      userCredentials: { mpin: this.mpinControl.value }
    }).subscribe({
      next: async (res: any) => {
        this.loader.hide();
        if (res.statusCode === 200) {
          this.currentUserData.isMpinAdded = true;
          await this.localStorageService.setIndexedDbKey('currentUser', this.currentUserData);
          this.isMpinAdded = true;
          this.popup.afterEffectSuccessAndErrorDialog('success', this.isResetFlow ? 'MPIN reset successfully' : 'MPIN set successfully');

          // Login/reset flows just needed the MPIN captured — close instead of showing away reasons
          if (this.isLoginFlow || this.isResetFlow) {
            this.dialogRef.close(true);
          }
        } else {
          this.popup.afterEffectSuccessAndErrorDialog('error', res?.message || 'Failed to set MPIN');
        }
      },
      error: (err: any) => {
        this.loader.hide();
        const msg = err?.error?.message || 'Failed to set MPIN. Please try again.';
        this.popup.afterEffectSuccessAndErrorDialog('error', msg);
      }
    });
  }

  async selectOption(option: AwayOption): Promise<void> {
    if (option.key === AwayStatusComponent._OTHER_OPTION_KEY) {
      this.showOtherReason = true;
      this._scrollToOtherReasonBlock();
      return;
    }
    await this._startBreak(option, option.label);
  }

  // Re-run after the textarea is blurred empty too — the required-field error that then
  // appears grows the block by another line, pushing it back out of view.
  onOtherReasonBlur(): void {
    this._scrollToOtherReasonBlock();
  }

  private _scrollToOtherReasonBlock(): void {
    // Wait a tick for the *ngIf block (and any error message inside it) to render before scrolling.
    setTimeout(() => this.otherReasonBlock?.nativeElement.scrollIntoView({ behavior: 'smooth', block: 'end' }));
  }

  async confirmOtherReason(): Promise<void> {
    if (this.otherReasonControl.invalid) {
      this.otherReasonControl.markAsTouched();
      return;
    }

    const reason = this.otherReasonControl.value.trim();
    const otherOption = this.awayOptions.find(o => o.key === AwayStatusComponent._OTHER_OPTION_KEY)!;
    await this._startBreak(otherOption, reason, reason);
  }

  private async _startBreak(option: AwayOption, lockLabel: string, reason?: string): Promise<void> {
    const currentUser = await this.localStorageService.getKeyIndexedDbKey('currentUser');

    // Save start time immediately when break begins (before API response delay)
    const breakStartTime = Date.now();

    this.loader.show();
    this.tmsService.breakStart(currentUser?.id).subscribe({
      next: () => {
        this.loader.hide();
        this.tmsSocketService.markLocked(breakStartTime);
        // Lock screen shows the typed reason as its label for the "Other" option.
        this.tmsSocketService.saveAwayOption({ ...option, label: lockLabel });
        this.dialogRef.close();
        this.tmsSocketService.showLockOverlay();
      },
      error: (err: any) => {
        this.loader.hide();
        const msg = err?.error?.message || 'Failed to set away status. Please try again.';
        this.popup.successAndErrorDialog('error', msg);
      },
    });
  }
}